import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, ArrowLeft, ChevronLeft, ChevronRight, ScrollText } from 'lucide-react';
import { useAdminAuditLog } from '../hooks';

const PAGE_SIZE = 50;

export function AdminAuditLogPage() {
  const [usernameFilter, setUsernameFilter] = useState('');
  const [keywordFilter, setKeywordFilter] = useState('');
  const [appliedUsername, setAppliedUsername] = useState<string | undefined>();
  const [appliedKeyword, setAppliedKeyword] = useState<string | undefined>();
  const [page, setPage] = useState(0);

  const { entries, total, loading, error, refresh } = useAdminAuditLog({
    username: appliedUsername,
    keyword: appliedKeyword,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasNextPage = page < totalPages - 1;
  const hasPrevPage = page > 0;

  const handleApplyFilters = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setAppliedUsername(usernameFilter.trim() || undefined);
      setAppliedKeyword(keywordFilter.trim() || undefined);
      setPage(0);
    },
    [usernameFilter, keywordFilter],
  );

  const handleClearFilters = useCallback(() => {
    setUsernameFilter('');
    setKeywordFilter('');
    setAppliedUsername(undefined);
    setAppliedKeyword(undefined);
    setPage(0);
  }, []);

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div>
        <Link
          to="/a/arche_dfe_game_state/admin"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Admin
        </Link>
        <h1 className="text-2xl font-semibold">Audit Log</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Browse game state mutation history ({total} entries)
        </p>
      </div>

      <Card>
        <CardContent className="pt-4">
          <form onSubmit={handleApplyFilters} className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs text-muted-foreground block mb-1">Username</label>
              <input
                type="text"
                value={usernameFilter}
                onChange={(e) => setUsernameFilter(e.target.value)}
                placeholder="Filter by username..."
                className="w-full px-3 py-2 border rounded-md bg-background text-sm"
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs text-muted-foreground block mb-1">Keyword</label>
              <input
                type="text"
                value={keywordFilter}
                onChange={(e) => setKeywordFilter(e.target.value)}
                placeholder="Filter by keyword..."
                className="w-full px-3 py-2 border rounded-md bg-background text-sm"
              />
            </div>
            <Button type="submit" size="sm">
              Apply
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={handleClearFilters}>
              Clear
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={refresh}>
              Refresh
            </Button>
          </form>
        </CardContent>
      </Card>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12" />
          ))}
        </div>
      ) : error ? (
        <Card className="border-destructive">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      ) : entries.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <ScrollText className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">No audit entries found</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {appliedUsername || appliedKeyword
                ? 'No entries match your filters. Try adjusting or clearing them.'
                : 'No game state mutations have been recorded yet.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="border rounded-md overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-3 font-medium">Timestamp</th>
                  <th className="text-left p-3 font-medium">Username</th>
                  <th className="text-left p-3 font-medium">Keyword</th>
                  <th className="text-left p-3 font-medium">Action</th>
                  <th className="text-left p-3 font-medium">Version</th>
                  <th className="text-left p-3 font-medium">Size</th>
                  <th className="text-left p-3 font-medium">Actor</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.auditId} className="border-b last:border-b-0">
                    <td className="p-3 text-muted-foreground whitespace-nowrap">
                      {new Date(entry.createdAt).toLocaleString()}
                    </td>
                    {/* Username resolved server-side. Fallback to userId for
                        deleted users so the cell never goes blank. */}
                    <td className="p-3 text-xs max-w-[150px] truncate">
                      {entry.username ?? (
                        <span className="font-mono text-muted-foreground">{entry.userId}</span>
                      )}
                    </td>
                    <td className="p-3 font-mono text-xs">{entry.keyword}</td>
                    <td className="p-3">
                      <Badge variant="outline">{entry.action}</Badge>
                    </td>
                    <td className="p-3 text-muted-foreground">
                      {entry.oldVersion != null ? `v${entry.oldVersion}` : '-'}
                      {' → '}
                      {entry.newVersion != null ? `v${entry.newVersion}` : '-'}
                    </td>
                    <td className="p-3 text-muted-foreground">
                      {entry.dataSizeBytes != null
                        ? `${entry.dataSizeBytes.toLocaleString()} B`
                        : '-'}
                    </td>
                    <td className="p-3 text-xs max-w-[150px] truncate">
                      {entry.actorUsername ?? (
                        <span className="font-mono text-muted-foreground">{entry.actorId}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Page {page + 1} of {totalPages}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!hasPrevPage}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!hasNextPage}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
